import { useMemo } from 'react'
import { useSelector } from 'react-redux'
import { Translate } from 'react-redux-i18n'
import { Notification } from '../../../../../../../common/notification'
import { formatVal, valToPercent } from '../../../../../../../helpers/value'
import { EMPTY_VALUE } from '../../../../../../../utils/common'
import {
  selectDistributionScore,
  selectDistributionScoreDes,
} from '../../../../store/slice'
import classes from './index.module.scss'

const DistributionOfScoreTable = ({ isDynamicReport, sortBy }) => {
  const distributionScore = useSelector(selectDistributionScore)
  const distributionScoreDes = useSelector(selectDistributionScoreDes)
  const locale = useSelector((state) => state.i18n.locale)

  // Text description
  const { industryName, companySizeName, modelSegmentName } =
    distributionScoreDes
  const des = {
    modelSegment: {
      title: 'distributionOfScore.modelSegment',
      value: modelSegmentName,
    },
    industry: {
      title: 'distributionOfScore.industryDes',
      value: industryName,
    },
    companySize: {
      title: 'distributionOfScore.companySize',
      value: companySizeName,
    },
  }

  const rows = useMemo(() => {
    const arr = distributionScore?.[sortBy] || []
    if (arr.length) {
      return [
        {
          key: 'numberOfCompanies',
          label: 'distributionOfScore.numberOfCompanies',
          values: arr.map((item) => ({
            value: item.companyCount,
          })),
        },
        {
          key: 'perTotalCompanies',
          label: 'distributionOfScore.perTotalCompanies',
          values: arr.map((item) => ({
            value: item.density,
          })),
        },
      ]
    }
    return []
  }, [distributionScore, sortBy])

  if (!rows.length)
    return (
      <div style={{ height: 200 }}>
        <Notification />
      </div>
    )

  if (isDynamicReport) {
    return (
      <div className={classes.table}>
        <div className="dFlex mb1 mt1" style={{ lineHeight: '20px' }}>
          <div style={{ fontWeight: 600 }}>
            {des[sortBy]?.title && <Translate value={des[sortBy]?.title} />}
          </div>
          &nbsp;
          {des[sortBy]?.value || EMPTY_VALUE}
        </div>
        <table
          style={{
            width: '100%',
            borderCollapse: 'collapse',
            tableLayout: 'fixed',
          }}
        >
          <thead>
            <tr>
              <th
                style={{
                  textAlign: 'left',
                  padding: '4px',
                  width: '70px',
                }}
              >
                <Translate value="distributionOfScore.riskGrade" />
              </th>
              {distributionScore[sortBy]?.map((h, idx) => (
                <th
                  key={idx}
                  style={{
                    padding: '4px',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    fontSize: 10,
                  }}
                >
                  {h.riskGradeTypeCode}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, rowIdx) => (
              <tr key={rowIdx}>
                <td
                  style={{
                    padding: '4px 2px',
                    textAlign: 'left',
                    fontSize: 10,
                    width: '70px',
                  }}
                >
                  <Translate value={row.label} />
                </td>
                {row.values.map((cell, cellIdx) => (
                  <td
                    key={cellIdx}
                    colSpan={cell.colSpan || 1}
                    style={{
                      padding: '4px 2px',
                      textAlign: 'center',
                      fontSize: 10,
                    }}
                  >
                    {row.key === 'perTotalCompanies'
                      ? valToPercent(cell.value, locale, true)
                      : formatVal(cell.value, locale, false, 0)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )
  }

  return (
    <div className={classes.table}>
      <div className="dFlex mb1 mt1" style={{ lineHeight: '20px' }}>
        <div style={{ fontWeight: 600 }}>
          {des[sortBy]?.title && <Translate value={des[sortBy]?.title} />}
        </div>
        &nbsp;
        {des[sortBy]?.value || EMPTY_VALUE}
      </div>
      <table
        style={{
          width: '100%',
          borderCollapse: 'collapse',
          tableLayout: 'fixed',
        }}
      >
        <thead>
          <tr>
            <th
              style={{
                textAlign: 'left',
                padding: '4px',
                width: '150px',
              }}
            >
              <Translate value="distributionOfScore.riskGrade" />
            </th>
            {distributionScore[sortBy]?.map((h, idx) => (
              <th
                key={idx}
                style={{
                  padding: '4px',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {h.riskGradeTypeCode}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, rowIdx) => (
            <tr key={rowIdx}>
              <td
                style={{
                  padding: '4px 2px',
                  textWrap: 'nowrap',
                  textAlign: 'left',
                }}
              >
                <Translate value={row.label} />
              </td>
              {row.values.map((cell, cellIdx) => (
                <td
                  key={cellIdx}
                  colSpan={cell.colSpan || 1}
                  style={{
                    padding: '4px 2px',
                    textAlign: 'center',
                  }}
                >
                  {row.key === 'perTotalCompanies'
                    ? valToPercent(cell.value, locale, true)
                    : formatVal(cell.value, locale, false, 0)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

export default DistributionOfScoreTable
